import { ErrorHandler, Injectable, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { ErrorFilterService } from '../services/error-filter.service';
import { ConsoleErrorInterceptor } from '../utils/console-error-interceptor';

/**
 * ErrorHandler unificado para producción que:
 * 1. Filtra errores irrelevantes del ciclo de vida de la aplicación
 * 2. Reporta solo errores genuinos a Sentry, LogRocket y DataDog
 * 3. Mantiene toda la funcionalidad existente de monitoreo
 */
@Injectable()
export class ProductionErrorHandler implements ErrorHandler {
  private errorFilterService = inject(ErrorFilterService);

  constructor() {
    // El interceptor YA fue inicializado en main.ts con una instancia temporal de ErrorFilterService
    // Ahora actualizamos con la instancia singleton de Angular (inject devuelve el singleton)
    // Esto asegura que todos usen la misma instancia del servicio
    if (ConsoleErrorInterceptor.isActive()) {
      // Actualizar la referencia del servicio (el interceptor manejará esto internamente)
      ConsoleErrorInterceptor.initialize(this.errorFilterService);
    }
    // Si NO está activo, es porque estamos en desarrollo y no debe activarse
  }

  /**
   * Maneja todos los errores de la aplicación
   * @param error El error a procesar
   */
  handleError(error: any): void {
    // Evaluar si el error debe ser ignorado
    if (this.errorFilterService.shouldIgnoreError(error)) {
      // Error filtrado - no hacer nada
      // No console.error, no reportar a ningún servicio
      // Silencio total para evitar ruido en monitoreo
      return;
    }

    // Es un error real que debe ser reportado
    this.reportRealError(error);
  }

  /**
   * Reporta errores reales a todos los servicios de monitoreo
   * @param error El error real a reportar
   */
  private reportRealError(error: any): void {
    try {
      // 1. Reportar a Sentry con diálogo de feedback
      // Mantiene la funcionalidad actual de showDialog: true
      if (typeof Sentry.captureException === 'function') {
        const eventId = Sentry.captureException(error);

        // Mostrar el diálogo de Sentry para que el usuario pueda dar feedback
        // Esto mantiene la funcionalidad actual de showDialog: true
        if (typeof Sentry.showReportDialog === 'function' && eventId) {
          // Solo mostrar el diálogo si hay una sesión activa
          const client = Sentry.getClient();
          const scope = Sentry.getCurrentScope();
          const user = scope.getUser();

          if (client && user?.email) {
            // Usar eventId para asociar el diálogo con el error específico
            Sentry.showReportDialog({
              eventId,
              user: {
                email: user.email,
                name: user.name || undefined,
              },
            });
          }
        }
      }
    } catch (sentryError) {
      // Si Sentry falla, no queremos que eso cause otro error
      // Usar el método original para evitar el interceptor
      const originalWarn = ConsoleErrorInterceptor.isActive()
        ? ConsoleErrorInterceptor.getOriginalWarn()
        : console.warn;
      originalWarn('Error al reportar a Sentry:', sentryError);
    }

    // 2. LogRocket eliminado del portal (2026-08-26): Sentry y DataDog cubren
    // el reporte de errores.

    // 3. Log a consola (marcado como [ProductionError] para indicar que ya fue procesado)
    // El ConsoleErrorInterceptor no filtrará mensajes que empiecen con [ProductionError]
    // porque ya pasaron por el filtrado del ErrorHandler
    console.error('[ProductionError]', error);

    // 4. Log adicional con información útil para debugging
    if (error?.stack) {
      console.error('[ProductionError] Stack trace:', error.stack);
    }

    // 5. Si el error tiene información adicional, registrarla
    if (error?.rejection) {
      console.error('[ProductionError] Promise rejection:', error.rejection);
    }

    if (error?.error && typeof error.error === 'object') {
      console.error('[ProductionError] Inner error:', error.error);
    }
  }
}